import { resolveCanAssignTechLead } from '@/presentation/pages/developments/utils/resolve-can-assign-tech-lead.js';
import type { UserProfile } from '@/shared/utils/format-status.js';

export type DetailPermissionsUser = { isGlobalAdmin: boolean; profile: UserProfile } | null | undefined;

export type DetailPermissions = {
    canApproveEstimate: boolean;
    canReturnEstimateToAnalysis: boolean;
    canSubmitEstimate: boolean;
    canAssignTechLead: boolean;
    canViewSuggestedEstimates: boolean;
    canViewDevelopmentTab: boolean;
    canEditDevelopers: boolean;
    canRegisterStart: boolean;
    isCf: boolean;
    isUc: boolean;
    isGpc: boolean;
    isDev: boolean;
    canFinalizeDev: boolean;
    canManageSolicitations: boolean;
    canResolveSolicitation: boolean;
    canEvaluateSolicitation: boolean;
    canDeleteDev: boolean;
    canCancelDev: boolean;
    canReactivateDev: boolean;
    canImpedeDev: boolean;
    canResumeDev: boolean;
    canLinkDev: boolean;
    canRetryJira: boolean;
};

export function resolveDetailPermissions(user: DetailPermissionsUser): DetailPermissions {
    const isGlobalAdmin = Boolean(user?.isGlobalAdmin);
    const hasProfile = (...profiles: UserProfile[]): boolean => isGlobalAdmin || (user ? profiles.includes(user.profile) : false);

    const isCf = hasProfile('CF');
    const isUc = hasProfile('UC');
    const isGpc = hasProfile('GPC');

    return {
        // CF passa a aprovar/rejeitar estimativa (matriz EP08-US04) — mesmos dois perfis nas
        // duas rotas de estimativa.
        canApproveEstimate: hasProfile('GPC', 'CF'),
        canReturnEstimateToAnalysis: hasProfile('GPA', 'LT'),
        canSubmitEstimate: hasProfile('GPA', 'LT'),
        // Regra própria (GPA + LT) centralizada em `resolveCanAssignTechLead` — não é `hasProfile('GPA', 'LT')`
        // por coincidência: a lista de perfis é compartilhada com a listagem de developments.
        canAssignTechLead: resolveCanAssignTechLead(user),
        canViewSuggestedEstimates: hasProfile('DEV', 'LT', 'GPA'),
        // Esta governa a visibilidade da aba, `canRegisterStart` governa a ação de registrar início
        // — mantidas separadas de propósito. O DEV vê a aba (time),
        // mas não registra o início.
        canViewDevelopmentTab: hasProfile('LT', 'DEV', 'GPA'),
        canEditDevelopers: hasProfile('LT', 'GPA'),
        // DEV removido da ação de registrar início — matriz e épico ("somente GPA ou LT"). A rota
        // `POST /:id/start` aceita `('GPA','LT')`.
        canRegisterStart: hasProfile('LT', 'GPA'),
        isCf,
        isUc,
        isGpc,
        isDev: hasProfile('DEV'),
        canFinalizeDev: hasProfile('GPA', 'LT', 'DEV'),
        canManageSolicitations: isCf || isUc || isGpc,
        canResolveSolicitation: isCf,
        canEvaluateSolicitation: hasProfile('GPA', 'LT'),
        canDeleteDev: hasProfile('CF'),
        // Matriz perfil-x-acao.md:167 nega cancelar ao GPA (ele só reativa) — as duas flags divergem
        // de propósito.
        canCancelDev: hasProfile('GPC', 'CF'),
        canReactivateDev: hasProfile('GPC', 'GPA', 'CF'),
        canImpedeDev: hasProfile('GPC', 'CF'),
        canResumeDev: hasProfile('GPC', 'GPA', 'CF'),
        // PGC: vínculo via PUT /:id, restrito ao campo linkedDevelopmentId — o CF já cobre o
        // vínculo através da edição plena (isCf/canEditDev), então esta flag é só para os não-CF.
        canLinkDev: hasProfile('GPA', 'GPC', 'LT'),
        canRetryJira: hasProfile('CF', 'GPA', 'LT')
    };
}
